/**
 * LUNA Coding Tutor - API Service
 * 원본: config.js, api.js의 모든 API 호출을 axios로 재구현
 * 
 * ===== API 매핑 테이블 (원본 → React) =====
 * 
 * [AI 생성 관련]
 * fetchCurriculum()           → api.generateCurriculum()
 * requestLessonPlan()         → api.generateLessonPlan()
 * requestProblem()            → api.generateProblem()
 * generateRoadmap()           → api.generateRoadmap()
 * requestAppliedChallenge()   → api.requestAppliedChallenge()
 * consultLearningPath()       → api.consultLearningPath() [신규]
 * 
 * [채팅 관련]
 * chatWithTutor()             → api.chat()
 * chatWithTutorStream()       → api.chatStream()
 * fetchLessonExplanation()    → api.getLessonExplanation()
 * 
 * [코드 실행/검증]
 * runPython()                 → api.runCode()
 * simulate()                  → api.simulate()
 * validate()                  → api.validate()
 * gradeCode()                 → api.gradeCode()
 * feedback()                  → api.feedback()
 * 
 * [세션/진행 상태]
 * startSession()              → api.startSession()
 * saveProgress()              → api.saveProgress()
 * saveStateToServer()         → api.saveState()
 * nextStep()                  → api.nextStep()
 * completeLesson()            → api.completeLesson()
 * 
 * [로드맵 관련]
 * getTopicProgress()          → api.getTopicProgress()
 * setTotalSteps()             → api.setTotalSteps()
 * completeStep()              → api.completeStep()
 * unlockNextTopic()           → api.unlockNextTopic()
 * skipStep()                  → api.skipStep()
 * 
 * [사용자 상태]
 * getUserStatus()             → api.getUserStatus()
 * checkAuth()                 → api.checkAuth()
 */

import axios from 'axios';

// ============================================
// 설정 상수 (원본 config.js 참조)
// ============================================

/**
 * API 베이스 URL 결정 로직 (원본 getApiBase() 함수와 동일)
 */
function getApiBase() {
  // 1. 환경 변수 (Vercel 배포 시 최우선)
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }

  // 2. URL 파라미터로 API 주소 명시적 지정 가능 (디버깅용)
  const urlParams = new URLSearchParams(window.location.search);
  const apiParam = urlParams.get('api');
  if (apiParam) {
    console.log('[LUNA API] URL 파라미터로 API 주소 설정:', apiParam);
    return apiParam;
  }

  const currentHost = window.location.hostname;
  const currentPort = window.location.port;

  // Vite 개발 서버 (port 5173) 또는 localhost인 경우 Proxy 사용
  if (currentHost === 'localhost' || currentHost === '127.0.0.1' || currentPort === '5173') {
    return '/api/luna';  // Vite proxy 사용 (vite.config.js에서 5000포트로 포워딩)
  }

  // 운영 환경 (dreamofenc.com)
  if (currentHost.includes('dreamofenc.com')) {
    return 'https://api.dreamofenc.com/api/luna';
  }

  // GitHub Pages 등 외부 호스팅
  if (currentHost.includes('github.io')) {
    return 'https://api.dreamofenc.com/api/luna';
  }

  // 네트워크 IP로 접속한 경우 (Backend Port 5000 사용)
  return `http://${currentHost}:5000/api/luna`;
}

export const API_BASE = getApiBase();

/**
 * 타임아웃 상수 (원본 TIMEOUT 객체와 동일)
 */
export const TIMEOUT = {
  DEFAULT: 15000,        // 일반 요청: 15초
  AUTH: 10000,           // 인증 요청: 10초
  AI_GENERATION: 120000, // AI 생성 작업: 120초 (로드맵, 커리큘럼, 레슨)
  AI_CHAT: 60000,        // AI 채팅: 60초
};

// ============================================
// Axios 인스턴스 생성
// ============================================

/**
 * 기본 API 클라이언트
 */
export const apiClient = axios.create({
  baseURL: API_BASE,
  timeout: TIMEOUT.DEFAULT,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,  // 세션 쿠키 전송 (원본 credentials: 'include')
});

/**
 * AI 생성 전용 API 클라이언트 (긴 타임아웃)
 */
export const aiClient = axios.create({
  baseURL: API_BASE,
  timeout: TIMEOUT.AI_GENERATION,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

/**
 * 채팅 전용 API 클라이언트
 */
export const chatClient = axios.create({
  baseURL: API_BASE,
  timeout: TIMEOUT.AI_CHAT,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// ============================================
// 인터셉터 설정
// ============================================

/**
 * 응답 에러 처리 인터셉터
 * 원본의 에러 처리 로직을 통합
 */
const handleResponseError = (error) => {
  if (axios.isCancel(error)) {
    // 요청 취소 (타임아웃 등)
    const message = error.message || '요청이 취소되었습니다.';
    return Promise.reject(new Error(message));
  }

  if (error.code === 'ECONNABORTED') {
    // 타임아웃
    return Promise.reject(new Error('요청 시간이 초과되었습니다. 네트워크 상태를 확인해주세요.'));
  }

  if (error.response) {
    // 서버 응답 에러
    const { status, data } = error.response;

    // 401 Unauthorized - 세션 만료 또는 미인증 (로그인/로그아웃 요청 자체는 제외)
    if (status === 401) {
      const url = error.config?.url || '';
      const isAuthEndpoint = url.includes('/login') || url.includes('/logout') || url.includes('/check_auth');
      if (!isAuthEndpoint) {
        // Zustand 스토어 초기화 후 로그인 페이지로 이동
        import('../store/useLunaStore').then(({ default: useLunaStore }) => {
          useLunaStore.getState().clearUserSession();
        });
        window.location.href = '/app';
        return Promise.reject(new Error('세션이 만료되었습니다. 다시 로그인해 주세요.'));
      }
    }

    // Rate Limit 초과 (429)
    if (status === 429) {
      return Promise.reject(new Error('AI 요청 한도를 초과했습니다. 잠시 후 다시 시도해주세요.'));
    }

    // 405 Method Not Allowed - 로그인 리다이렉트 문제일 가능성 높음
    if (status === 405 && error.config.url.includes('login')) {
      return Promise.reject(new Error('로그인이 필요합니다. (세션 만료)'));
    }

    // 서버가 보낸 에러 메시지 추출
    const errorMsg = data?.error_message || data?.error || `HTTP ${status} 오류`;
    return Promise.reject(new Error(errorMsg));
  }

  if (error.request) {
    // 네트워크 에러
    return Promise.reject(new Error('서버에 연결할 수 없습니다. 네트워크를 확인해주세요.'));
  }

  return Promise.reject(error);
};

// 인터셉터 적용
[apiClient, aiClient, chatClient].forEach(client => {
  client.interceptors.response.use(
    response => response,
    handleResponseError
  );
});

// ============================================
// API 함수들 (원본 api.js 1:1 매핑)
// ============================================

/**
 * 커리큘럼 생성
 * 원본: fetchCurriculum(topic)
 * 
 * @param {string} topic - 학습 주제
 * @returns {Promise<Object>} { topic, curriculum, is_broad?, clarification_question?, suggested_topics? }
 */
export async function generateCurriculum(topic) {
  const response = await aiClient.post('/curriculum', { topic });
  const data = response.data;

  // 서버가 error 플래그를 보낸 경우 (Magic or Nothing)
  if (data.error) {
    throw new Error(data.error_message || '커리큘럼 생성에 실패했습니다.');
  }

  return data;
}

/**
 * 레슨 설명 요청
 * 원본: fetchLessonExplanation()
 * 
 * @param {Object} params - { curriculum, currentStep, chatHistory }
 * @returns {Promise<string>} 설명 텍스트
 */
export async function getLessonExplanation({ curriculum, currentStep, chatHistory }) {
  const specificQuestion = `좋습니다. 이제 다음 단계인 "${currentStep?.title || '다음 주제'}"에 대해 설명해주세요.`;

  const response = await chatClient.post('/chat', {
    problem: {},
    user_code: '',
    chat_history: chatHistory,
    question: specificQuestion,
    curriculum,
    current_step: currentStep,
  });

  return response.data.answer || '설명을 가져오지 못했습니다.';
}

/**
 * AI 튜터 채팅 (일반)
 * 원본: chatWithTutor(payload)
 * 
 * @param {Object} payload - 채팅 요청 데이터
 * @returns {Promise<Object>} { answer, new_problem? }
 */
export async function chat(payload) {
  const response = await chatClient.post('/chat', payload);
  return response.data;
}

/**
 * AI 튜터 채팅 (스트리밍)
 * 원본: chatWithTutorStream(payload, onChunk, onComplete)
 * 
 * @param {Object} payload - 채팅 요청 데이터
 * @param {Function} onChunk - 텍스트 조각 콜백 (chunk: string) => void
 * @param {Function} onComplete - 완료 콜백 (fullText: string) => void
 * @returns {Promise<Object>} { answer }
 */
export async function chatStream(payload, onChunk, onComplete) {
  const response = await fetch(`${API_BASE}/chat_stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error('AI 채팅 응답에 실패했습니다.');
  }

  const contentType = response.headers.get('Content-Type') || '';

  // JSON 응답인 경우 (첫 대화)
  if (contentType.includes('application/json')) {
    const data = await response.json();
    if (onComplete) onComplete(data.answer);
    return data;
  }

  // NDJSON 스트리밍 파싱 (원본 로직 동일)
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let fullText = '';

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    const chunk = decoder.decode(value, { stream: true });
    buffer += chunk;

    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (!line.trim()) continue;

      try {
        const jsonChunk = JSON.parse(line);

        if (jsonChunk.type === 'chunk' && jsonChunk.content) {
          fullText += jsonChunk.content;
          if (onChunk) onChunk(jsonChunk.content);
        } else if (jsonChunk.type === 'end') {
          if (onComplete) onComplete(fullText);
        }
      } catch (e) {
        console.warn('[스트리밍] JSON 파싱 건너뜀:', line.substring(0, 50));
      }
    }
  }

  return { answer: fullText };
}

/**
 * 문제 생성
 * 원본: requestProblem(topic, preferredLanguage, stepDescription)
 * 
 * @param {string} topic - 주제
 * @param {string} preferredLanguage - 선호 언어 (python, bash, html 등)
 * @param {string} stepDescription - 단계 설명
 * @returns {Promise<Object>} 문제 데이터
 */
export async function generateProblem(topic, preferredLanguage, stepDescription) {
  const response = await aiClient.post('/problem', {
    topic,
    preferred_language: preferredLanguage,
    step_description: stepDescription,
  });

  const data = response.data;

  if (data.error) {
    throw new Error(data.error_message || '문제 생성에 실패했습니다.');
  }

  return data;
}

/**
 * 수업 계획 생성
 * 원본: requestLessonPlan(topic, lesson_title, previous_lessons, roadmap_context)
 * 
 * @param {Object} params - 요청 파라미터
 * @returns {Promise<Object>} 레슨 플랜 데이터
 */
export async function generateLessonPlan({
  topic,
  lessonTitle,
  previousLessons = null,
  roadmapContext = null,
  currentStepIndex = null, // [신규] 현재 단원 인덱스 - DB fallback 대신 이 값 우선 사용
}) {
  const payload = { topic, lesson_title: lessonTitle };

  // Scaffolding: 이전 단계 학습 정보 전달
  if (previousLessons && previousLessons.length > 0) {
    payload.previous_lessons = previousLessons;
  }

  // 로드맵 컨텍스트 전달
  if (roadmapContext) {
    payload.roadmap_context = roadmapContext;
  }

  // [신규] 현재 단원 인덱스 명시적 전달 - stale DB 값 사용 방지
  if (currentStepIndex !== null && currentStepIndex !== undefined) {
    payload.current_step_index = currentStepIndex;
  }

  const response = await aiClient.post('/lesson_plan', payload);
  const data = response.data;

  if (data.error) {
    throw new Error(data.error_message || '수업 계획 생성에 실패했습니다.');
  }

  return data;
}


/**
 * 로드맵 생성
 * 원본: postAIGeneration('/roadmap/generate', { goal })
 * 
 * @param {string} goal - 학습 목표
 * @param {string} type - 로드맵 타입 (career 또는 course)
 * @returns {Promise<Object>} 로드맵 데이터
 */
export async function generateRoadmap(goal, type = 'career') {
  const response = await aiClient.post('/roadmap/generate', {
    goal,
    type // [신규] 'career' or 'course'
  });
  const data = response.data;

  if (data.error) {
    throw new Error(data.error_message || '로드맵 생성에 실패했습니다.');
  }

  return data;
}

/**
 * 학습 경로 상담 (신규)
 * 원본: consultLearningPath(userInput)
 * @param {string} userInput - 사용자 입력
 * @returns {Promise<Object>} { short_term: [], long_term: [] }
 */
export async function consultLearningPath(userInput) {
  const response = await aiClient.post('/consult', { user_input: userInput });
  const data = response.data;

  if (data.error) {
    throw new Error(data.error_message || '상담 데이터 생성에 실패했습니다.');
  }
  return data;
}

/**
 * 상태 저장
 * 원본: saveStateToServer()
 * 
 * @param {string} userId - 사용자 ID
 * @param {Object} state - 저장할 상태
 * @returns {Promise<Object>} 저장 결과
 */
export async function saveState(userId, state) {
  const response = await apiClient.post('/save_state', {
    user_id: userId,
    state,
  });
  return response.data;
}

/**
 * 코드 실행 (Python) -> [Deprecated] Client-side Pyodide로 대체됨
 * 원본: runPython(code)
 * 
 * @param {string} code - 실행할 코드
 * @returns {Promise<Object>} { stdout, stderr }
 */
export async function runCode(code) {
  console.warn('[Security] Backend execution is deprecated. Please use Pyodide service.');
  // return apiClient.post('/run', { code }); // Legacy call
  throw new Error('Backend code execution is disabled for security. Please refresh the page to load Pyodide.');
}

/**
 * 코드 시뮬레이션
 * 원본: simulate(problem, user_code)
 * 
 * @param {Object} problem - 문제 데이터
 * @param {string} userCode - 사용자 코드
 * @returns {Promise<Object>} 시뮬레이션 결과
 */
export async function simulate(problem, userCode) {
  const response = await apiClient.post('/simulate', {
    problem,
    user_code: userCode,
  });
  return response.data;
}

/**
 * 코드 검증
 * 원본: validate(problem, user_code)
 * 
 * @param {Object} problem - 문제 데이터
 * @param {string} userCode - 사용자 코드
 * @returns {Promise<Object>} { is_correct, message }
 */
export async function validate(problem, userCode) {
  const response = await apiClient.post('/validate', {
    problem,
    user_code: userCode,
  });
  return response.data;
}

/**
 * 코드 채점
 * 원본: gradeCode(problem, user_code)
 * 
 * @param {Object} problem - 문제 데이터
 * @param {string} userCode - 사용자 코드
 * @param {string} userId - 사용자 ID (XP 보상용)
 * @param {string} topic - 현재 토픽 (옵션)
 * @param {number} stepIndex - 현재 단계 (옵션)
 * @param {boolean} isDemo - 체험 레슨 여부
 * @param {string} gradingStrategy - "lite" or "full" (Phase 2)
 * @param {string} executionResult - 로컬 실행 결과 (Pyodide/샌드박스)
 * @returns {Promise<Object>} 채점 결과
 */
export async function gradeCode(problem, userCode, userId, topic = null, stepIndex = null, isDemo = false, gradingStrategy = 'full', executionResult = null) {
  const response = await aiClient.post('/grade', {
    problem,
    user_code: userCode,
    user_id: userId,
    topic,
    step_index: stepIndex,
    is_demo: isDemo,
    grading_strategy: gradingStrategy,
    execution_result: executionResult, // [신규] 로컬 실행 결과 전달
  });
  return response.data;
}

/**
 * [Phase 3] 빠른 채점 (AI 없음, 로컬 실행 + 출력 비교)
 * 
 * @param {Object} problem - 문제 데이터 (expected_output 포함 필요)
 * @param {string} userCode - 사용자 코드
 * @returns {Promise<Object>} { is_correct, needs_ai, feedback, output }
 */
export async function quickGrade(problem, userCode) {
  const response = await apiClient.post('/quick_grade', {
    problem,
    user_code: userCode,
  });
  return response.data;
}

/**
 * 레슨 완료 기록
 * 원본: completeLesson(topic, lessonTitle, summary, userCode)
 * 
 * @param {Object} params - 완료 정보
 * @returns {Promise<Object>} 기록 결과
 */
export async function completeLesson({ topic, lessonTitle, summary, userCode, learnedKeywords }) {
  try {
    const response = await apiClient.post('/complete_lesson', {
      topic: topic || '',
      lesson_title: lessonTitle || '',
      summary: summary || '',
      user_code: userCode || '',
      learned_keywords: learnedKeywords || [], // [Fix] 학습 키워드 전달
    });
    return response.data;
  } catch (error) {
    console.warn('[API] 레슨 완료 기록 실패:', error);
    return null;
  }
}

/**
 * 토픽 진행 상태 조회
 * 원본: getJSON(`/roadmap/topic_progress?phase_index=${pIdx}&topic_index=${tIdx}`)
 * 
 * @param {number} phaseIndex - 페이즈 인덱스
 * @param {number} topicIndex - 토픽 인덱스
 * @returns {Promise<Object>} 진행 상태
 */
export async function getTopicProgress(phaseIndex, topicIndex) {
  const response = await apiClient.get('/roadmap/topic_progress', {
    params: { phase_index: phaseIndex, topic_index: topicIndex },
  });
  return response.data;
}

/**
 * 전체 스텝 수 설정
 * 원본: postJSON('/roadmap/set_total_steps', {...})
 */
export async function setTotalSteps(phaseIndex, topicIndex, totalSteps) {
  const response = await apiClient.post('/roadmap/set_total_steps', {
    phase_index: phaseIndex,
    topic_index: topicIndex,
    total_steps: totalSteps,
  });
  return response.data;
}

/**
 * 스텝 완료 처리
 * 원본: postJSON('/roadmap/complete_step', {...})
 */
export async function completeStep(phaseIndex, topicIndex, stepIndex) {
  const response = await apiClient.post('/roadmap/complete_step', {
    phase_index: phaseIndex,
    topic_index: topicIndex,
    step_index: stepIndex,
  });
  return response.data;
}

/**
 * 다음 토픽 언락
 * 원본: postJSON('/roadmap/unlock_next', {...})
 */
export async function unlockNextTopic(phaseIndex, topicIndex, topicId = null) {
  const response = await apiClient.post('/roadmap/unlock_next', {
    phase_index: phaseIndex,
    topic_index: topicIndex,
    topic_id: topicId, // [New]
  });
  return response.data;
}

/**
 * 스텝 건너뛰기
 * 원본: postJSON('/roadmap/skip_step', {...})
 */
export async function skipStep(phaseIndex, topicIndex, stepIndex) {
  const response = await apiClient.post('/roadmap/skip_step', {
    phase_index: phaseIndex,
    topic_index: topicIndex,
    step_index: stepIndex,
  });
  return response.data;
}

/**
 * 사용자 상태 조회
 * 원본: getJSON('/user/status')
 */
export async function getUserStatus() {
  const response = await apiClient.get('/user/status');
  return response.data;
}

// ============================================
// 추가 API 함수들 (원본 api.js 100% 매핑)
// ============================================

/**
 * 피드백 요청
 * 원본: feedback(problem, user_code, result, rendered_html)
 * 
 * @param {Object} problem - 문제 데이터
 * @param {string} userCode - 사용자 코드
 * @param {Object} result - 실행 결과
 * @param {string} renderedHtml - 렌더링된 HTML (HTML 문제용)
 * @returns {Promise<Object|null>} 피드백 결과
 */
export async function feedback(problem, userCode, result, renderedHtml) {
  try {
    const response = await apiClient.post('/feedback', {
      problem,
      user_code: userCode,
      result,
      rendered_html: renderedHtml,
    });
    return response.data;
  } catch (error) {
    console.warn('[API] feedback 실패:', error);
    return null;
  }
}

/**
 * 세션 시작/복원
 * 원본: startSession(user_id, topic)
 * 
 * @param {string} userId - 사용자 ID
 * @param {string} topic - 학습 주제
 * @returns {Promise<Object|null>} 세션 데이터 또는 null
 */
export async function startSession(userId, topic) {
  try {
    const response = await apiClient.post('/start_session', {
      user_id: userId,
      topic,
    });
    return response.data;
  } catch (error) {
    console.warn('[API] startSession 실패:', error);
    return null;
  }
}

/**
 * 인증 확인
 * @returns {Promise<Object>} { authenticated: boolean, user: Object|null }
 */
export async function checkAuth() {
  try {
    const response = await apiClient.get('/user/status');
    // 서버 응답에서 authenticated 필드 확인
    if (response.data.authenticated && response.data.user) {
      return { authenticated: true, user: response.data.user };
    }
    return { authenticated: false, user: null };
  } catch (error) {
    return { authenticated: false, user: null };
  }
}

/**
 * 진척도 저장
 * 원본: saveProgress(user_id, step_index)
 * 
 * @param {string} userId - 사용자 ID
 * @param {number} stepIndex - 현재 스텝 인덱스
 * @returns {Promise<Object|null>} 저장 결과
 */
export async function saveProgress(userId, stepIndex) {
  try {
    const response = await apiClient.post('/save_progress', {
      user_id: userId,
      step_index: stepIndex,
    });
    return response.data;
  } catch (error) {
    console.warn('[API] saveProgress 실패:', error);
    return null;
  }
}

/**
 * 정답 후 다음 단계 통합 안내 요청
 * 원본: nextStep(correct_problem, next_step)
 * 
 * @param {Object} correctProblem - 정답 처리된 문제
 * @param {Object} nextStep - 다음 단계 정보
 * @returns {Promise<Object>} 다음 단계 안내 응답
 */
export async function nextStep(correctProblem, nextStepInfo) {
  const response = await apiClient.post('/next_step', {
    correct_problem: correctProblem,
    next_step: nextStepInfo,
  });
  return response.data;
}

/**
 * 응용 과제 요청
 * 원본: requestAppliedChallenge(problem, user_code)
 * 
 * @param {Object} problem - 기본 문제 데이터
 * @param {string} userCode - 사용자가 작성한 코드
 * @returns {Promise<Object>} 응용 과제 데이터
 */
export async function requestAppliedChallenge(problem, userCode, context = []) {
  const response = await aiClient.post('/applied_challenge', {
    problem,
    user_code: userCode,
    context, // [New]
  });

  const data = response.data;

  if (data.error) {
    throw new Error(data.error_message || '응용 과제 생성에 실패했습니다.');
  }

  return data;
}

// ============================================
// 대시보드 관련 API (원본 dashboard.js 참조)
// ============================================

/**
 * 대시보드 데이터 조회
 * 원본: getJSON('/dashboard')
 * 
 * @returns {Promise<Object>} 대시보드 전체 데이터
 */
export async function getDashboard() {
  const response = await apiClient.get('/dashboard');
  return response.data;
}

/**
 * 로드맵 조회 (대시보드 프리뷰용)
 * 원본: getJSON('/roadmap')
 * 
 * @returns {Promise<Object>} 로드맵 데이터
 */
export async function getRoadmap() {
  const response = await apiClient.get('/roadmap');
  return response.data;
}

/**
 * 로드맵 초기화
 * 원본: POST /roadmap/reset
 * 
 * @returns {Promise<Object>} 결과
 */
export async function resetRoadmap() {
  const response = await apiClient.post('/roadmap/reset');
  return response.data;
}

/**
 * 사용자 메시지 목록 조회
 * 원본: getJSON('/user/messages')
 * 
 * @returns {Promise<Object>} { messages: [], unread_count: number }
 */
export async function getUserMessages() {
  const response = await apiClient.get('/user/messages');
  return response.data;
}

/**
 * 메시지 읽음 처리
 * 원본: fetch(`${API_BASE}/user/messages/${id}/read`, { method: 'POST' })
 * 
 * @param {string|number} messageId - 메시지 ID
 * @returns {Promise<Object>} 결과
 */
export async function markMessageAsRead(messageId) {
  const response = await apiClient.post(`/user/messages/${messageId}/read`);
  return response.data;
}

/**
 * 모든 메시지 읽음 처리
 * 원본: fetch(`${API_BASE}/user/messages/read-all`, { method: 'POST' })
 * 
 * @returns {Promise<Object>} 결과
 */
export async function markAllMessagesAsRead() {
  const response = await apiClient.post('/user/messages/read-all');
  return response.data;
}

/**
 * 저장된 상태 불러오기 (서버에서)
 * 원본: getJSON('/load_state')
 * 
 * @returns {Promise<Object|null>} 저장된 상태 또는 null
 */
export async function loadStateFromServer() {
  try {
    const response = await apiClient.get('/load_state');
    const state = response.data?.state || null;

    if (state) {
      // [Fix] 샘플 데모 커리큘럼이 저장되어 있으면 무시 (로그인 사용자에게 데모 데이터 복원 방지)
      const curriculum = state.currentCurriculum;
      if (curriculum?.topic === 'Python 기초: 변수와 데이터' &&
        curriculum?.curriculum?.length === 3 &&
        curriculum?.curriculum?.[0]?.title?.includes('데이터 담는 상자')) {
        console.log('[API] 샘플 데모 커리큘럼 감지 - 무시하고 실제 커리큘럼 대기');
        state.currentCurriculum = null;
      }
    }

    return state;
  } catch (error) {
    console.warn('[API] loadStateFromServer 실패:', error);
    return null;
  }
}

// ============================================
// 인증 관련 API (원본 login.html 참조)
// ============================================

/**
 * 로그인
 * @param {string} username - 사용자명
 * @param {string} password - 비밀번호
 * @returns {Promise<Object>} 로그인 결과
 */
export async function login(username, password) {
  const response = await apiClient.post('/login', { username, password });
  return response.data;
}

/**
 * 로그아웃
 * @returns {Promise<Object>} 로그아웃 결과
 */
export async function logout() {
  const response = await apiClient.post('/logout');
  return response.data;
}

/**
 * 회원가입
 * @param {Object} userData - { username, password, nickname }
 * @returns {Promise<Object>} 회원가입 결과
 */
export async function register(userData) {
  const response = await apiClient.post('/register', userData);
  return response.data;
}

/**
 * 비밀번호 변경
 * @param {string} currentPassword - 현재 비밀번호
 * @param {string} newPassword - 새 비밀번호
 * @param {string} confirmPassword - 확인 비밀번호
 * @returns {Promise<Object>} 변경 결과
 */
export async function changePassword(currentPassword, newPassword, confirmPassword) {
  const response = await apiClient.post('/change_password', {
    current_password: currentPassword,
    new_password: newPassword,
    confirm_password: confirmPassword,
  });
  return response.data;
}

/**
 * 프로필(닉네임) 변경
 * @param {string} nickname - 새 닉네임
 * @returns {Promise<Object>} 변경 결과
 */
export async function updateProfile(nickname) {
  const response = await apiClient.post('/user/profile', { nickname });
  return response.data;
}

// ============================================
// 관리자 API (원본 admin.html 참조)
// ============================================

/**
 * 사용자 목록 조회 (관리자 전용)
 * @returns {Promise<Object>} { users: [] }
 */
export async function getAdminUsers() {
  const response = await apiClient.get('/admin/users');
  return response.data;
}

/**
 * 사용자 승인 (관리자 전용)
 * @param {number} userId - 사용자 ID
 * @returns {Promise<Object>} 결과
 */
export async function approveUser(userId) {
  const response = await apiClient.post('/admin/approve', { user_id: userId });
  return response.data;
}

/**
 * 사용자 승인 취소 (관리자 전용)
 * @param {number} userId - 사용자 ID
 * @returns {Promise<Object>} 결과
 */
export async function revokeUser(userId) {
  const response = await apiClient.post('/admin/revoke', { user_id: userId });
  return response.data;
}

/**
 * 관리자 권한 토글 (관리자 전용)
 * @param {number} userId - 사용자 ID
 * @returns {Promise<Object>} 결과
 */
export async function toggleAdmin(userId) {
  const response = await apiClient.post('/admin/toggle_admin', { user_id: userId });
  return response.data;
}

/**
 * 사용자 삭제 (관리자 전용)
 * @param {number} userId - 사용자 ID
 * @returns {Promise<Object>} 결과
 */
export async function deleteUser(userId) {
  const response = await apiClient.post('/admin/delete', { user_id: userId });
  return response.data;
}

/**
 * 관리자 메시지 전송 (관리자 전용)
 * @param {Array<string>|string} userIds - 수신자 ID 목록 또는 "all"
 * @param {string} message - 메시지 내용
 */
export async function adminSendMessage(userIds, message) {
  const response = await apiClient.post('/admin/send_message', {
    user_ids: userIds,
    message: message
  });
  return response.data;
}

/**
 * 관리자가 특정 유저에게 이메일 발송 (관리자 전용)
 * @param {string} toEmail - 수신자 이메일
 * @param {string} subject - 제목
 * @param {string} body - 본문
 * @returns {Promise<Object>} 결과
 */
export async function adminSendEmail(toEmail, subject, body) {
  const response = await apiClient.post('/admin/send_email', {
    to_email: toEmail,
    subject,
    body,
  });
  return response.data;
}

export async function adminSendEmailAll(subject, body) {
  const response = await apiClient.post('/admin/send_email_all', { subject, body });
  return response.data;
}

/**
 * 사용자 학습 데이터 초기화 (관리자 전용)
 * 레벨, XP, 로드맵, 커리큘럼, 배지, 레슨로그, 능력치 초기화 (계정은 유지)
 * @param {number} userId - 사용자 ID
 * @returns {Promise<Object>} 결과
 */
export async function resetUserProgress(userId) {
  const response = await apiClient.post('/admin/reset_progress', { user_id: userId });
  return response.data;
}

/**
 * 랭킹 조회 (Leaderboard)
 * @returns {Promise<Object>} { leaderboard: [] }
 */
export async function getLeaderboard() {
  const response = await apiClient.get('/leaderboard');
  return response.data;
}

/**
 * 관리자 분석 데이터 조회
 * @returns {Promise<Object>} { kpi, charts, users }
 */
export async function getAdminAnalytics() {
  const response = await apiClient.get('/admin/analytics');
  return response.data;
}

/**
 * 관리자 AI 토큰 사용량 조회
 * @param {Object} params
 * @param {string} [params.period] - "7d" | "30d" | "90d" | "all" (default: "30d")
 * @param {string} [params.user_id] - 특정 사용자 필터 (optional)
 * @returns {Promise<Object>} { kpi, by_user, by_day, by_endpoint }
 */
export async function getAdminTokenUsage({ period = '30d', user_id } = {}) {
  const params = { period };
  if (user_id) params.user_id = user_id;
  const response = await apiClient.get('/admin/token_usage', { params });
  return response.data;
}

// 기본 내보내기
export default {
  generateCurriculum,
  getLessonExplanation,
  chat,
  chatStream,
  generateProblem,
  generateLessonPlan,
  generateRoadmap,
  consultLearningPath, // [신규]
  saveState,
  runCode,
  simulate,
  validate,
  gradeCode,
  completeLesson,
  getTopicProgress,
  setTotalSteps,
  completeStep,
  unlockNextTopic,
  skipStep,
  getUserStatus,
  // 추가된 API
  feedback,
  startSession,
  checkAuth, // [신규]
  saveProgress,
  nextStep,
  requestAppliedChallenge,
  // 대시보드 API
  getDashboard,
  getRoadmap,
  resetRoadmap,
  getUserMessages,
  markMessageAsRead,
  markAllMessagesAsRead,
  loadStateFromServer,
  // 인증 API
  login,
  logout,
  register,
  changePassword,
  updateProfile,
  // 관리자 API
  getAdminUsers,
  approveUser,
  revokeUser,
  toggleAdmin,
  deleteUser,
  adminSendMessage,
  adminSendEmail,
  adminSendEmailAll,
  resetUserProgress,
  getLeaderboard,
  getAdminAnalytics,
  getAdminTokenUsage,
};
